// @flow strict

import * as React from 'react';
import useI18n from 'src/hooks/useI18n';

import {useApi} from 'src/hooks/useApi';

import {useDispatch, useSelector} from 'react-redux';
import {useInflight} from 'src/hooks/redux';

//$FlowFixMe[nonstrict-import]
import * as reduxApi from 'src/utils/redux-api-v2';

import {
  Toast,
  TOAST_SEMANTIC,
  toastApi,
  ToastTitle,
} from '@spaced-out/ui-design-system/lib/components/Toast';
import {CircularLoader} from '@spaced-out/ui-design-system/lib/components/CircularLoader';

import {getFeedbackConfiguration} from 'src/action-creators/crm';
import {selectFeedbackConfiguration} from 'src/selectors/crm';

import EvaluationFormView from './evaluation-form-view.jsx';
import EvaluationFormEdit from './evaluation-form-edit.jsx';

import css from './evaluation.css';

// $FlowFixMe[signature-verification-failure]
const Evaluation = ({
  review,
  onBehalfOfUserId,
  scorecard,
  sections,
  stepCandidate,
  step,
  onCancel,
  // redux props
  scorecardConfigData,
  isViewMode,
  setViewMode,
  updateData,
  jobId,
}): React.Node => {
  const {labelI18nInstance} = useI18n();
  const t = labelI18nInstance.t;

  const dispatch = useDispatch();
  const {id: stepId} = step;

  const [submittingReview, setSubmittingReview] = React.useState(false);

  const {result: jobScorecard} = useApi(
    {pathname: `/job-scorecards/${jobId}`},
    {
      method: 'GET',
      apiPath: '/trmapi/api',
    },
  );

  const {
    result: scorecardStepConfiguration,
    isLoading: isScoreCardConfigDataLoading,
  } = useApi(
    {pathname: `/step-scorecard-config?stepId=${stepId}`},
    {
      method: 'GET',
      apiPath: '/trmapi/api',
    },
  );

  const isFeedbackConfigurationLoading = useInflight(
    getFeedbackConfiguration(),
  );
  const feedbackConfiguration = useSelector(selectFeedbackConfiguration);

  React.useEffect(() => {
    if (!feedbackConfiguration) {
      dispatch(getFeedbackConfiguration());
    }
  }, [jobId, stepId]);

  const {sections: scorecardJobSections = []} = jobScorecard || {};

  const scorecardStepSections = scorecardJobSections
    .map((section) => {
      const foundStepSection = scorecardStepConfiguration?.find(
        (stepSection) => stepSection.sectionId === section.id,
      );

      return {
        ...section,
        status: foundStepSection ? foundStepSection.status : '',
      };
    })
    .filter((section) => section.status !== 'RESTRICTED');

  const handleSubmitForm = (evaluation) => {
    const {jobId, stageId, stepId, candidateId} = stepCandidate;

    const {hideOverallRecommendation, hideScorecard} = step;

    const newValues = {
      jobId,
      stageId,
      stepId,
      candidateId,
      ...evaluation,
      reviewerId: onBehalfOfUserId,
      interviewCandidateId: stepCandidate.id,
    };

    if (hideScorecard) {
      delete newValues.feedbackScores;
    }
    if (hideOverallRecommendation) {
      delete newValues.reviewStatus;
      delete newValues.comment;
    }

    const apiUrl = review
      ? `api/interview-reviews/${review.id}`
      : 'api/interview-reviews';

    setSubmittingReview(true);

    dispatch(
      reduxApi.post(
        apiUrl,
        {
          ...newValues,
        },
        {},
        {apiPath: '/trmapi/'},
      ),
    )
      .then(() => {
        toastApi.show(
          <Toast semantic={TOAST_SEMANTIC.success}>
            <ToastTitle>
              {review
                ? `${t(
                    'Feedback_Updated_Successfully',
                    'Feedback Updated Successfully',
                  )}!`
                : `${t(
                    'Feedback_Added_Successfully',
                    'Feedback Added Successfully',
                  )}!`}
            </ToastTitle>
          </Toast>,
        );
        updateData?.();
      })
      .catch((error) => {
        toastApi.show(
          <Toast semantic={TOAST_SEMANTIC.danger}>
            <ToastTitle>
              {t('Feedback_Update_Failed', 'Feedback Update Failed')}!
            </ToastTitle>
          </Toast>,
        );
      })
      .finally(() => {
        setSubmittingReview(false);
      });
  };

  if (isFeedbackConfigurationLoading || isScoreCardConfigDataLoading) {
    return <CircularLoader />;
  }

  if (isViewMode && review) {
    return (
      <div className={css.evaluationFormViewContainer}>
        <EvaluationFormView
          step={step}
          feedbackConfiguration={feedbackConfiguration}
          scorecardStepSections={scorecardStepSections}
          subjectiveSections={sections}
          review={review}
        />
      </div>
    );
  }

  return (
    <EvaluationFormEdit
      step={step}
      feedbackConfiguration={feedbackConfiguration}
      scorecardStepSections={scorecardStepSections}
      subjectiveSections={sections}
      review={review}
      stepCandidateId={stepCandidate.id}
      onSubmit={handleSubmitForm}
      alreadyInPanelBody={true}
      onCancel={onCancel}
      isSubmitting={submittingReview}
    />
  );
};

export default Evaluation;
